import { normalizeReturnURI } from '@/lib/redirectUtils';
import z from 'zod';

export const RETURN_URL_KEY = 'enrich-return-url';

export const protectedSearchSchema = z.object({
  signUp: z.string().optional(),
  state: z
    .object({
      returnTo: z.string().optional(),
    })
    .optional(),
  receipt: z.string().optional(),
  viewTx: z.string().optional(),
});

export type ProtectedSearch = z.infer<typeof protectedSearchSchema>;

export interface ProtectedBeforeLoadParams {
  auth: {
    isLoading: boolean;
    workOSUser: { id: string } | null;
    signIn: (opts: { state: { returnTo: string } }) => Promise<void>;
    signUp: (opts: { state: { returnTo: string } }) => Promise<void>;
  };
  search: ProtectedSearch;
  location: { pathname: string; searchStr: string };
}

export type ProtectedBeforeLoadResult = { redirect: string } | { pending: true } | void;

/**
 * Core beforeLoad logic for protected routes.
 * Extracted for testability - can be tested without TanStack Router internals.
 */
export async function protectedBeforeLoad({
  auth,
  search,
  location,
}: ProtectedBeforeLoadParams): Promise<ProtectedBeforeLoadResult> {
  if (search.state?.returnTo) {
    sessionStorage.setItem(RETURN_URL_KEY, search.state.returnTo);
    return;
  }

  if (auth.isLoading) {
    return;
  }

  if (auth.workOSUser) {
    const returnKey = sessionStorage.getItem(RETURN_URL_KEY);
    if (returnKey) {
      sessionStorage.removeItem(RETURN_URL_KEY);
      return { redirect: normalizeReturnURI(returnKey) };
    }
    return;
  }

  // Not logged in: kick off the WorkOS redirect (this sets window.location.href)
  // and signal the caller to hang the route on its pendingComponent until the
  // browser navigation completes — otherwise the router will race the redirect
  // by fetching the route's component chunk, which gets cancelled and surfaces
  // as a "Failed to fetch dynamically imported module" error.
  const returnTo = location.pathname + location.searchStr;
  if (search.signUp === 'true') {
    await auth.signUp({ state: { returnTo } });
  } else {
    await auth.signIn({ state: { returnTo } });
  }
  return { pending: true };
}
